import IconButton from 'components/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import moment from 'moment';
import React, { useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Button,
  Table,
  Input,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
} from 'reactstrap';

import CADetailModal from './CADetailModal';
import {
  caListingDataAtom,
  forceRerenderUIAtom,
  selectedCloudAgentsFilterAtom,
} from './state';
import history from '../../../../../history';
import { getDrillDownUrl, getItemFromLocalStorage } from '../../helper';
import { caRefreshAtom } from '../state';

const BASE_URL: string = getItemFromLocalStorage('approle') || '';

const RowActions = ({ rowData }) => {
  const selectedCloudAgentsFilter = useAtomValue(selectedCloudAgentsFilterAtom);

  const caListingData = useAtomValue(caListingDataAtom);
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    caId: 0,
    deletedBy: '',
    deletedDate: '',
  });
  const [openAliasModal, setOpenAliasModal] = useState(false);
  const [alias, setalias] = useState('');
  const [aliasId, setaliasId] = useState(0);

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const forceReloadCAListing = useSetAtom(caRefreshAtom);
  const forceRerenderUI = useSetAtom(forceRerenderUIAtom);

  async function confirmAliasModal() {
    setOpenAliasModal(false);
    let url = 'cloud_agents/' + aliasId + '/';
    await callApi(url, 'PATCH', {}, { alias: alias.trim() })
      .then((res) => {
        if (res.status === 200) {
          if (res.data) {
            rowData['alias'] = alias.trim();
            forceRerenderUI(Math.random().toString());
            showSuccessToast('Alias Updated successfully.');
          }
        } else {
          showFailureToast(`Failed to update alias. ${res.status}`);
        }
      })
      .catch((err) => {
        let res = err.response;
        let error = 'Unknown error occurred. Please try again.';
        if (res && res.data) {
          if (res.data.detail.includes('duplicate')) {
            error =
              'An alias with same name is already assigned to another Cloud Agent. Please choose a different alias name.';
          } else {
            error = res.data.detail;
          }
          showFailureToast(error);
        }
      });
  }

  const handleCADelete = async (id) => {
    setIsDeleting(true);
    await callApi(`cloud_agents/${id}/delete/`, 'PATCH', {}, { deleted: true })
      .then((res) => {
        if (res.status === 204) {
          // update the listing data and counts
          let updatedcaListingData = caListingData.items.filter(
            (x) => x.id !== id,
          );
          caListingData.items = updatedcaListingData;
          caListingData.count = updatedcaListingData.length;
          showSuccessToast('Cloud Agent deleted successfully.');
        } else {
          showFailureToast(`Failed to delete cloud agent. ${res.status}`);
        }
      })
      .catch((err) => {
        let res = err.response;
        let error = 'Unknown error occured. Please try again.';
        if (res && res.data) {
          error = res.data.detail;
          showFailureToast(error);
        }
      })
      .finally(() => {
        setIsDeleting(false);
        setOpenConfirmDeleteModal(false);
        closeDetailModal();
        forceReloadCAListing(Math.random().toString());
      });
  };
  const closeDetailModal = () => {
    setDetailModal({ ...detailModal, isOpen: false });
  };

  const renderDeleteConfirmModal = () => {
    return (
      <Modal isOpen={openConfirmDeleteModal} size="sm">
        <ModalHeader>
          <h4>Delete Confirmation</h4>
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete this Cloud Agent?</p>

          <label style={{ color: 'dodgerblue' }}>
            Note: This operation is <strong>irreversible</strong>. It will
            remove all vulnerabilities and related data of this Cloud Agent.
          </label>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setOpenConfirmDeleteModal(false)}
          >
            Cancel
          </Button>
          {isDeleting ? (
            <CircleSpinner size={20} color="#0665a9" loading={true} />
          ) : (
            <Button
              color="danger"
              disabled={isDeleting}
              onClick={() => handleCADelete(detailModal.caId)}
            >
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  };

  const renderDetailModal = () => {
    return (
      <Modal isOpen={detailModal.isOpen} toggle={closeDetailModal} size="lg">
        <ModalHeader toggle={closeDetailModal}>
          <h4>Cloud Agent Details</h4>
        </ModalHeader>
        <ModalBody>
          {detailModal.caId > 0 && <CADetailModal id={detailModal.caId} />}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => closeDetailModal()}>
            Cancel
          </Button>
          {selectedCloudAgentsFilter !== 'deleted' ? (
            atob(localStorage.getItem(btoa('approle')) || btoa('client')) ===
              'admin' && (
              <Button
                color="danger"
                onClick={() => setOpenConfirmDeleteModal(true)}
              >
                Delete
              </Button>
            )
          ) : (
            <p>
              <strong>
                This Cloud Agent is deleted by {detailModal.deletedBy} at{' '}
                {moment(detailModal.deletedDate).format('lll')}
              </strong>
            </p>
          )}
        </ModalFooter>
      </Modal>
    );
  };

  const openDetailModal = (caId, deletedBy, deletedDate) => {
    setDetailModal({
      isOpen: true,
      caId: caId,
      deletedBy: deletedBy,
      deletedDate: deletedDate,
    });
  };

  const id: number = rowData['id'];
  const deleted: boolean = selectedCloudAgentsFilter === 'deleted';
  const deletedBy = rowData['deleted_cloudagent__deleted_by'];
  const deletedDate = rowData['deleted_cloudagent__deleted_at'];

  return (
    <div className="text-nowrap">
      {/* Only show for active CA*/}
      {!deleted && (
        <>
          <IconButton
            id="editAlias"
            iconClass="fa fa-edit"
            size="sm"
            tooltip="Edit Alias"
            onClick={() => {
              setOpenAliasModal(true);
              setalias(rowData['alias']);
              setaliasId(rowData['id']);
            }}
          />
          <IconButton
            id="vulnLink"
            iconClass="fa fa-bug"
            size="sm"
            tooltip="View Vulnerabilities"
            onClick={() =>
              history.push(
                getDrillDownUrl({
                  type: 'open',
                  sourceType:
                    rowData['server_or_workstation'] === 'Server'
                      ? 'ca_data'
                      : 'ca_data-workstations',
                  hosts_in: [rowData.id],
                  severity: '',
                  issueType: '',
                  qid: '',
                  severity_in: undefined,
                }),
                // `/${BASE_URL}/qualys/vulnerabilities?source_type=${
                //   rowData['server_or_workstation'] === 'Server'
                //     ? 'ca_data'
                //     : 'ca_data-workstations'
                // }&cloudagent_id=${id}`,
              )
            }
          />
        </>
      )}
      <IconButton
        id="detailsLink"
        iconClass="fa fa-eye"
        size="sm"
        tooltip="View Cloud Agent Details"
        onClick={() => openDetailModal(id, deletedBy, deletedDate)}
      />
      {/* NOTE: Always conditionally render modal dialogs for performance */}
      {detailModal.isOpen && renderDetailModal()}
      {openConfirmDeleteModal && renderDeleteConfirmModal()}
      {openAliasModal && (
        <Modal isOpen={openAliasModal} size="lg">
          <ModalHeader>Edit Cloud Agent Alias</ModalHeader>
          <ModalBody>
            <Table className="tablesorter table-striped" responsive>
              <tbody>
                <tr>
                  <td>Alias</td>
                  <td>
                    <Input
                      type="text"
                      value={alias}
                      placeholder="Alias (Max length 255 characters)"
                      maxLength={255}
                      onChange={(e) => {
                        setalias(e.target.value);
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setOpenAliasModal(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={() => confirmAliasModal()}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default RowActions;
